import {
  CustomerListFilters,
  CustomerRole,
  CustomerStatus,
  CustomerRoleEnum,
  CustomerPermissionEnum,
  CustomerRoleOnly,
  CustomerPermissionOnly,
  OpoRoleEnum,
  OpoRoleOnly,
} from '@/api/apis/customer';

export const ORGANIZATION_TENANT_PREFIX = {
  TxCenter: 'txcenter',
  OPO: 'opo',
} as const;

export const CUSTOMER_ROLE_NAMES: Record<CustomerRoleOnly, string> = {
  [CustomerRole.TxCenterAdministrator]: 'TX Center Administrator',
  [CustomerRole.TxCenterCoordinator]: 'TX Center Coordinator',
  [CustomerRole.ReceivingSurgeon]: 'Receiving Surgeon',
  [CustomerRole.AdminAuditor]: 'Admin Auditor',
  [CustomerRole.TxCenterTransplantSupport]: 'Transplant Support',
} as const;

export const CUSTOMER_ROLE_ADMIN_NAMES: Record<CustomerRoleOnly, string> = {
  [CustomerRole.TxCenterAdministrator]: 'Administrator',
  [CustomerRole.TxCenterCoordinator]: 'Coordinator',
  [CustomerRole.ReceivingSurgeon]: 'Surgeon',
  [CustomerRole.AdminAuditor]: 'Admin Auditor',
  [CustomerRole.TxCenterTransplantSupport]: 'Transplant Support',
} as const;

export const CUSTOMER_PERMISSION_NAMES: Record<CustomerPermissionOnly, string> = {
  [CustomerRole.TxCenterQuoteApprover]: 'Quote Approver',
  [CustomerRole.TxCenterOrganApprover]: 'Organ Approver',
  [CustomerRole.TxCenterOfferCoordinator]: 'Offer Coordinator',
} as const;

export const CUSTOMER_ROLE_DESCRIPTIONS: Record<CustomerRoleOnly, string> = {
  [CustomerRole.TxCenterAdministrator]:
    'Can manage the organization, users, OPOs, view quotes and invoices, access archive.',
  [CustomerRole.TxCenterCoordinator]: 'Can manage OPOs and perform donor organ clinical acceptance.',
  [CustomerRole.ReceivingSurgeon]: 'Can perform donor organ clinical acceptance.',
  [CustomerRole.TxCenterTransplantSupport]:
    'Can review assigned cases, and access the General chat, no editing any information.',
  [CustomerRole.AdminAuditor]:
    'Can view the quote and invoices, and access to the Quote Chat, no editing any information.',
} as const;

export const CUSTOMER_ROLE_DESCRIPTIONS_EUROPE = {
  [CustomerRole.TxCenterAdministrator]: 'Can manage the organization and users, access archive.',
  [CustomerRole.TxCenterCoordinator]:
    'Can create cases, manage case teams and perform donor organ clinical acceptance.',
  [CustomerRole.ReceivingSurgeon]: 'Can perform donor organ clinical acceptance and edit Organ Acceptance Criteria.',
} as const satisfies Partial<Record<CustomerRoleOnly, string>>;

export const CUSTOMER_PERMISSION_DESCRIPTIONS: Record<CustomerPermissionOnly, string> = {
  [CustomerRole.TxCenterQuoteApprover]:
    'Quote Approver grants additional access to interact with and view quote information when added to another role.',
  [CustomerRole.TxCenterOrganApprover]: 'The Organ Approver permission allows to approve organ on OCS.',
  [CustomerRole.TxCenterOfferCoordinator]:
    'Offer Coordinator grants access to view, create, and manage organ offers when added to another role.',
} as const;

// Offer Coordinator is not covered by any base role, so it is intentionally absent here.
export const CUSTOMER_PERMISSION_COVERED_IN_ROLE: Partial<Record<CustomerPermissionOnly, CustomerRoleOnly>> = {
  [CustomerPermissionEnum.TxCenterQuoteApprover]: CustomerRoleEnum.TxCenterAdministrator,
  [CustomerPermissionEnum.TxCenterOrganApprover]: CustomerRoleEnum.ReceivingSurgeon,
};

// Permission can't be removed if user has only one role that covers it.
export const CUSTOMER_PERMISSION_MANDATORY_IN_ROLE: Partial<Record<CustomerPermissionOnly, CustomerRoleOnly>> = {
  [CustomerPermissionEnum.TxCenterQuoteApprover]: CustomerRoleEnum.TxCenterAdministrator,
};

// if ReceivingSurgeon is selected, then TxCenterOrganApprover is selected by default but can be deselected
export const CUSTOMER_ROLE_DEFAULT_PERMISSIONS: Partial<Record<CustomerRoleOnly, CustomerPermissionOnly[]>> = {
  [CustomerRoleEnum.ReceivingSurgeon]: [CustomerPermissionEnum.TxCenterOrganApprover],
};

// if ReceivingSurgeon or TxCenterCoordinator is selected, then TxCenterQuoteApprover is allowed for selection
export const ALLOWED_CUSTOMER_PERMISSION_SELECTION_FOR_ROLE: Partial<
  Record<CustomerPermissionOnly, CustomerRoleOnly[]>
> = {
  [CustomerPermissionEnum.TxCenterQuoteApprover]: [
    CustomerRoleEnum.ReceivingSurgeon,
    CustomerRoleEnum.TxCenterCoordinator,
  ],
  [CustomerPermissionEnum.TxCenterOrganApprover]: [
    CustomerRoleEnum.TxCenterAdministrator,
    CustomerRoleEnum.TxCenterCoordinator,
    CustomerRoleEnum.ReceivingSurgeon,
  ],
  [CustomerPermissionEnum.TxCenterOfferCoordinator]: [
    CustomerRoleEnum.TxCenterAdministrator,
    CustomerRoleEnum.TxCenterCoordinator,
    CustomerRoleEnum.ReceivingSurgeon,
  ],
};

export const CUSTOMER_ROLES_EXCLUSIVE: CustomerRoleOnly[] = [
  CustomerRole.AdminAuditor,
  CustomerRole.TxCenterTransplantSupport,
];

// Permissions that can never be assigned from the customer TX user form. They render disabled when a
// user already holds one, and are hidden entirely otherwise
export const CUSTOMER_PERMISSIONS_NON_ASSIGNABLE: CustomerPermissionOnly[] = [
  CustomerPermissionEnum.TxCenterOfferCoordinator,
];

export const CUSTOMER_STATUS_NAMES = {
  [CustomerStatus.Initialized]: 'Invite Sent',
  [CustomerStatus.InviteSent]: 'Invite Sent',
  [CustomerStatus.Verified]: ['Active', 'Inactive'],
} as const;

export const defaultTxUserListFilters = (isVerified: boolean): CustomerListFilters => ({
  txCenters: [],
  roles: [],
  isPrimary: undefined,
  enabled: undefined,
  isVerified,
});

export const CUSTOMER_ROLE_WEIGHT: Record<CustomerRoleOnly, number> = {
  [CustomerRoleEnum.TxCenterAdministrator]: 0,
  [CustomerRoleEnum.TxCenterCoordinator]: 1,
  [CustomerRoleEnum.ReceivingSurgeon]: 2,
  [CustomerRoleEnum.AdminAuditor]: 3,
  [CustomerRoleEnum.TxCenterTransplantSupport]: 4,
};

export const CUSTOMER_PERMISSION_WEIGHT: Record<CustomerPermissionOnly, number> = {
  [CustomerPermissionEnum.TxCenterQuoteApprover]: 0,
  [CustomerPermissionEnum.TxCenterOrganApprover]: 1,
  [CustomerPermissionEnum.TxCenterOfferCoordinator]: 2,
};

export const OPO_ROLE_NAMES: Record<OpoRoleOnly, string> = {
  [OpoRoleEnum.OpoAdmin]: 'OPO Administrator',
  [OpoRoleEnum.OpoRetrievalSurgeon]: 'OPO Retrieval Surgeon',
  [OpoRoleEnum.OpoTransplantCoordinator]: 'OPO Transplant Coordinator',
  [OpoRoleEnum.OpoTransplantSupport]: 'OPO Transplant Support',
} as const;

export const OPO_ROLE_DESCRIPTIONS: Record<OpoRoleOnly, string> = {
  [OpoRoleEnum.OpoAdmin]: 'Can manage the OPO organization and users.',
  [OpoRoleEnum.OpoRetrievalSurgeon]: 'Performs organ retrieval surgery.',
  [OpoRoleEnum.OpoTransplantCoordinator]: 'Can manage donor cases within the OPO.',
  [OpoRoleEnum.OpoTransplantSupport]: 'Provides transplant support within the OPO.',
} as const;

export const OPO_ROLE_WEIGHT: Record<OpoRoleOnly, number> = {
  [OpoRoleEnum.OpoAdmin]: 0,
  [OpoRoleEnum.OpoRetrievalSurgeon]: 1,
  [OpoRoleEnum.OpoTransplantCoordinator]: 2,
  [OpoRoleEnum.OpoTransplantSupport]: 3,
};

export const OPO_CONTACT_ROLE_NAMES: Record<OpoRoleEnum, string> = {
  [OpoRoleEnum.OpoAdmin]: 'Administrator',
  [OpoRoleEnum.OpoTransplantCoordinator]: 'Coordinator',
  [OpoRoleEnum.OpoRetrievalSurgeon]: 'Surgeon',
  [OpoRoleEnum.OpoTransplantSupport]: 'Support',
};
